import {
  isEmail,
  matchField,
  required,
  type Validator,
} from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Button,
  TextField,
  Stack,
  PasswordField,
} from 'braid-design-system';
import React, { type FormEventHandler } from 'react';

import CurrentPasswordNotRequired from 'src/components/Account/CurrentPasswordNotRequired/CurrentPasswordNotRequired';
import ServerError from 'src/components/Account/ServerError/ServerError';
import type { ErrorMessage } from 'src/types';

import DontHavePasswordLink from '../../DontHavePassword/DontHavePasswordLinkContainer';

import translations from './.vocab';
import type { EmailUseField } from './FormContainer';

export const FORM_NAME = 'changeEmail';

interface Props {
  onSubmit: FormEventHandler;
  toggle: () => void;
  submitting: boolean;
  error?: ErrorMessage;
  useField: EmailUseField;
  showCustomerServiceView: boolean;
}

const Form = ({
  onSubmit,
  toggle,
  submitting,
  error,
  useField,
  showCustomerServiceView,
}: Props) => {
  const { t } = useTranslations(translations);

  const newEmail = useField({
    id: 'newEmail',
    validators: [isEmail],
  });
  const confirmEmail = useField({
    id: 'confirmEmail',
    validators: [
      matchField as Validator<
        string,
        {
          matchFieldName: string;
          matchErrorMessage: string;
        },
        Record<string, any>,
        string
      >,
    ],
    validatorProps: {
      matchFieldName: 'newEmail',
      matchErrorMessage: t('The email does not match'),
    },
  });
  const currentPassword = useField({
    id: 'currentPassword',
    validators: showCustomerServiceView ? [] : [required],
  });

  return (
    <form onSubmit={onSubmit}>
      <Stack space="xlarge">
        <Stack space="medium">
          <TextField
            id="newEmail"
            name="newEmail"
            label={t('Email address')}
            type="email"
            reserveMessageSpace
            {...newEmail}
          />

          <TextField
            id="confirmEmail"
            name="confirmEmail"
            label={t('Confirm email address')}
            type="email"
            reserveMessageSpace
            {...confirmEmail}
          />
          {showCustomerServiceView ? (
            <CurrentPasswordNotRequired />
          ) : (
            <Stack space="small">
              <PasswordField
                id="currentPassword"
                name="currentPassword"
                label={t('Password')}
                {...currentPassword}
              />
              <DontHavePasswordLink originator="change email" />
            </Stack>
          )}
        </Stack>

        {error && !submitting ? <ServerError error={error} /> : null}
        <Actions>
          <Button type="submit" tone="formAccent" loading={submitting}>
            {t('Save')}
          </Button>
          <Button variant="transparent" tone="formAccent" onClick={toggle}>
            {t('Cancel')}
          </Button>
        </Actions>
      </Stack>
    </form>
  );
};

export default Form;
